import {useCallback, useState} from "react";
import {useFlags} from "launchdarkly-react-client-sdk";
import {
  SearchOperationalEntitiesData,
  SearchOperationalEntitiesVariables
} from "../../backend";
import {useGraphqlLazyQuery, AmplifyClientKey} from "../../../amplify/hooks";
import {searchOperationalEntities as searchOperationalEntitiesQuery} from "../../../amplify/graphql";
import {features} from "../../../features";
import {OperationalTableData, toOperationalTableData} from "../search/table-data";

const PHASE_1_RESULT_CAP = 500;

const LUCENE_RESERVED = /[+\-&|!(){}[\]^"~*?:\\/\s]/g;

function escapeLucene(value: string): string {
  return value.replace(LUCENE_RESERVED, "\\$&");
}

export interface UseTenantOrgsResult {
  orgs: OperationalTableData[];
  loading: boolean;
  error?: Error;
  truncated: boolean;
  fetch: () => Promise<void>;
}

export function useTenantOrgs(
  tenantId: string | undefined
): UseTenantOrgsResult {
  const [orgs, setOrgs] = useState<OperationalTableData[]>([]);
  const [truncated, setTruncated] = useState(false);

  const flags = useFlags();
  const useSpringBffPassthrough =
    flags[features.dev.springBffPassthrough.searchAndHierarchy];
  const amplifyClient: AmplifyClientKey = useSpringBffPassthrough
    ? "appsyncBff"
    : "springBff";

  const [search, {loading, error}] = useGraphqlLazyQuery<
    SearchOperationalEntitiesData,
    SearchOperationalEntitiesVariables
  >({amplifyQuery: searchOperationalEntitiesQuery, amplifyClient});

  const fetch = useCallback(async () => {
    if (!tenantId) return;

    const result = await search({
      variables: {query: `tenantid:${escapeLucene(tenantId)}`}
    });

    const results = result.data?.searchResult?.results ?? [];
    setOrgs(results.map(toOperationalTableData));
    setTruncated(results.length === PHASE_1_RESULT_CAP);
  }, [tenantId, search]);

  return {orgs, loading, error, truncated, fetch};
}
